// Share a dashboard page with people outside InRange: staff add them by name and email, name
// them in place, and remove them after a confirmation. Nothing is emailed; whoever adds
// someone sends them the link.
// docs/Y2026-Q4/design-amazon-overview-sharing.md, design-amazon-item-owners.md section 2.2
import { useContext, useEffect, useRef, useState, type FormEvent } from "react";

import {
  type PageShare,
  useAddShare,
  useNameShare,
  useRemoveShare,
  useShareList,
} from "../api/hooks/useShares";
import { UserContext } from "../auth";
import icon from "./IconButton.module.css";
import styles from "./SharePanel.module.css";

const day = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** "First Last", or "" for someone shared before names were kept. */
const shareName = (s: Pick<PageShare, "first_name" | "last_name">): string =>
  [s.first_name, s.last_name].filter(Boolean).join(" ");

const PersonPlus = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="9" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2 21c0-4 3.1-6.5 7-6.5s7 2.5 7 6.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M19 8v6M16 11h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/** First and last name, edited in place on a person already on the list. */
const NameForm = ({
  share,
  pageKey,
  onDone,
}: {
  share: PageShare;
  pageKey: string;
  onDone: () => void;
}) => {
  const name = useNameShare(pageKey);
  const [first, setFirst] = useState(share.first_name ?? "");
  const [last, setLast] = useState(share.last_name ?? "");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!first.trim() || !last.trim()) return;
    name.mutate(
      { email: share.email, first_name: first.trim(), last_name: last.trim() },
      { onSuccess: onDone },
    );
  };
  return (
    <form className={styles.nameForm} onSubmit={submit}>
      <input aria-label="First name" placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} />
      <input aria-label="Last name" placeholder="Last name" value={last} onChange={(e) => setLast(e.target.value)} />
      <button type="submit" disabled={name.isLoading || !first.trim() || !last.trim()}>
        Save
      </button>
      <button type="button" className={styles.remove} onClick={onDone}>
        Cancel
      </button>
      {name.isError && <div className={styles.error}>{name.error.message}</div>}
    </form>
  );
};

/** The Share button and its panel; renders nothing for anyone but staff. ``path`` is the
 * page the copied link opens; ``assignedTo`` says how many items a person owns there, which
 * removing them leaves assigned. */
export const ShareButton = ({
  pageKey,
  path,
  assignedTo,
}: {
  pageKey: string;
  path: string;
  assignedTo: (email: string) => number;
}) => {
  const { role } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [naming, setNaming] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PageShare | null>(null);
  const [copied, setCopied] = useState(false);
  const shares = useShareList(pageKey, open && role === "admin");
  const add = useAddShare(pageKey);
  const remove = useRemoveShare(pageKey);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (role !== "admin") return null;

  const ready = Boolean(email.trim() && first.trim() && last.trim());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    add.mutate(
      { email: email.trim(), first_name: first.trim(), last_name: last.trim() },
      {
        onSuccess: () => {
          setEmail("");
          setFirst("");
          setLast("");
        },
      },
    );
  };
  const link = `${window.location.origin}${path}`;
  const copy = () =>
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });

  return (
    <div className={styles.root} ref={root}>
      <button
        type="button"
        className={icon.iconButton}
        aria-label="Share"
        aria-expanded={open}
        title="Share"
        onClick={() => setOpen(!open)}
      >
        <PersonPlus />
      </button>
      {open && (
        <div className={styles.panel} role="dialog" aria-label="Who has access">
          <div className={styles.title}>Who has access</div>
          <div className={styles.muted}>Only InRange staff see this list.</div>
          <form className={styles.add} onSubmit={submit}>
            <div className={styles.names}>
              <input
                placeholder="First name"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                aria-label="First name"
              />
              <input
                placeholder="Last name"
                value={last}
                onChange={(e) => setLast(e.target.value)}
                aria-label="Last name"
              />
            </div>
            <div className={styles.names}>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Email address"
              />
              <button type="submit" disabled={add.isLoading || !ready}>
                Add
              </button>
            </div>
          </form>
          {add.isError && <div className={styles.error}>{add.error.message}</div>}
          {shares.isLoading && <div className={styles.muted}>Loading…</div>}
          {shares.isError && <div className={styles.error}>Could not load who has access.</div>}
          <ul className={styles.list}>
            {(shares.data ?? []).map((s) =>
              naming === s.email ? (
                <li key={s.email}>
                  <NameForm share={s} pageKey={pageKey} onDone={() => setNaming(null)} />
                </li>
              ) : removing?.email === s.email ? (
                <li key={s.email} className={styles.confirm}>
                  <div>
                    Remove {shareName(s) || s.email}? They lose access on their next page load.
                    {assignedTo(s.email) > 0 &&
                      ` Their ${assignedTo(s.email)} assigned ${
                        assignedTo(s.email) === 1 ? "item stays" : "items stay"
                      } assigned to them, marked "no access", so you can reassign ${
                        assignedTo(s.email) === 1 ? "it" : "them"
                      }.`}
                  </div>
                  <div className={styles.confirmActions}>
                    <button
                      type="button"
                      className={styles.danger}
                      disabled={remove.isLoading}
                      onClick={() =>
                        remove.mutate(s.email, { onSuccess: () => setRemoving(null) })
                      }
                    >
                      Remove access
                    </button>
                    <button type="button" className={styles.remove} onClick={() => setRemoving(null)}>
                      Cancel
                    </button>
                  </div>
                </li>
              ) : (
                <li key={s.email}>
                  <div>
                    <div>{shareName(s) || s.email}</div>
                    <div className={styles.muted}>
                      {shareName(s) ? `${s.email} · ` : ""}Added by {s.added_by} · {day(s.added_at)}
                    </div>
                  </div>
                  <div className={styles.rowActions}>
                    <button type="button" className={styles.remove} onClick={() => setNaming(s.email)}>
                      {shareName(s) ? "Edit name" : "Add name"}
                    </button>
                    <button type="button" className={styles.remove} onClick={() => setRemoving(s)}>
                      Remove
                    </button>
                  </div>
                </li>
              ),
            )}
          </ul>
          {remove.isError && <div className={styles.error}>{remove.error.message}</div>}
          <div className={styles.footer}>
            <div className={styles.muted}>
              InRange staff always have access. No email is sent: send them the link yourself.
            </div>
            <button type="button" onClick={copy}>
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
